import React from 'react';
import { useTranslation } from 'react-i18next';
import { Database } from 'lucide-react';

interface ChartNoDataProps {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    size?: 'tiny' | 'small' | 'medium' | 'large';
}

/**
 * Shared Empty State for all charts.
 * Optimized for Dashbal aesthetic: clean, minimal, and premium.
 */
export const ChartNoData: React.FC<ChartNoDataProps> = ({
    title,
    description,
    icon,
    size = 'large'
}) => {
    const { t } = useTranslation('common');

    const isTiny = size === 'tiny';
    const isSmall = size === 'small';

    // In tiny containers, we show only a minimal text indicator to avoid any overlap or cropping
    if (isTiny) {
        return (
            <div className="w-full h-full flex items-center justify-center p-1 text-neutral-400/60 dark:text-neutral-500/60">
                <span className="text-[10px] font-bold uppercase tracking-wider truncate">
                    {t('charts.noData.short', 'Empty')}
                </span>
            </div>
        );
    }

    return (
        <div className={`w-full h-full flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-500 overflow-hidden ${
            isSmall ? 'p-3 gap-2' : 'p-6 gap-3'
        }`}>
            {/* Visual Indicator */}
            {!isSmall && (
                <div className="w-11 h-11 rounded-2xl bg-neutral-50 dark:bg-neutral-800/40 flex items-center justify-center border border-neutral-100 dark:border-neutral-700/50 shadow-sm shrink-0 mb-1">
                    {icon || <Database size={20} strokeWidth={1.5} className="text-neutral-300 dark:text-neutral-600" />}
                </div>
            )}
            
            <div className="flex flex-col items-center max-w-[220px] shrink">
                <h4 className={`${
                    isSmall ? 'text-[10px]' : 'text-[11px]'
                } font-bold text-neutral-700 dark:text-neutral-300 px-2 leading-tight`}>
                    {title || t('charts.noData.title', 'Analysis Pending')}
                </h4>
                
                {!isSmall && (
                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 leading-relaxed mt-1.5 px-4 line-clamp-3">
                        {description || t('charts.noData.description', "We couldn't find any records for this specific analysis.")}
                    </p>
                )}
            </div>
        </div>
    );
};
